import { FunctionComponent, useState } from 'react';
import { SURVEY_QUESTIONS, MILITARY_PAGE_COPY } from 'src/constants/militaryLanding';

export interface SurveyResponse {
  readonly questionId: string;
  readonly answer: 'yes' | 'no';
}

export interface SurveyResult {
  readonly visitorName: string;
  readonly responses: ReadonlyArray<SurveyResponse>;
}

interface SurveyPopupProps {
  readonly onComplete: (result: SurveyResult) => void;
}

const TOTAL_STEPS = SURVEY_QUESTIONS.length + 1;

const SurveyPopup: FunctionComponent<SurveyPopupProps> = ({ onComplete }) => {
  const [step, setStep] = useState<number>(0);
  const [visitorName, setVisitorName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [responses, setResponses] = useState<ReadonlyArray<SurveyResponse>>([]);

  const handleNameSubmit = (): void => {
    if (!visitorName.trim()) {
      setNameError('Please enter your name to continue.');
      return;
    }
    setNameError('');
    setStep(1);
  };

  const handleAnswer = (answer: 'yes' | 'no'): void => {
    const questionIndex = step - 1;
    const newResponse: SurveyResponse = {
      questionId: SURVEY_QUESTIONS[questionIndex].id,
      answer,
    };

    const updatedResponses = [...responses, newResponse];

    if (step < TOTAL_STEPS - 1) {
      setResponses(updatedResponses);
      setStep((prev) => prev + 1);
    } else {
      onComplete({
        visitorName: visitorName.trim(),
        responses: updatedResponses,
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5">
      <div className="bg-white rounded-xl p-5 w-full max-w-sm">

        <div className="flex gap-1 mb-3">
          {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
            <div
              key={index}
              className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                index <= step ? 'bg-primary' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {step === 0 ? (
          <>
            <p className="text-xs text-gray-400 mb-1">Step 1 of {TOTAL_STEPS}</p>
            <h3 className="text-base font-medium text-gray-900 mb-2 leading-snug">
              {MILITARY_PAGE_COPY.surveyIntro}
            </h3>
            <p className="text-sm text-gray-500 mb-4">What is your name?</p>
            <input
              type="text"
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
              placeholder="Your name"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 mb-1 focus:outline-none focus:border-primary"
            />
            {nameError && (
              <p className="text-xs text-danger mb-3">{nameError}</p>
            )}
            <button
              onClick={handleNameSubmit}
              className="w-full py-2.5 bg-primary text-white rounded-lg text-sm font-medium mt-3 hover:bg-primary-dark transition-colors duration-200"
            >
              Continue
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-1">
              Step {step + 1} of {TOTAL_STEPS}
            </p>
            <h3 className="text-base font-medium text-gray-900 mb-4 leading-snug">
              {SURVEY_QUESTIONS[step - 1].question}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleAnswer('yes')}
                className="py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 hover:bg-primary-light hover:border-primary transition-colors duration-200"
              >
                Yes
              </button>
              <button
                onClick={() => handleAnswer('no')}
                className="py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 hover:bg-primary-light hover:border-primary transition-colors duration-200"
              >
                No
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default SurveyPopup;

